import React, { useState, useEffect } from "react";
import {
  Gauge,
  Map,
  CloudFog,
  PanelTop,
  SatelliteDish,
  ActivitySquare,
  MapPin,
  CloudFogIcon,
  Zap,
  Shield,
  ArrowLeft,
  Building2,
  Moon,
  Sun,
  ChevronDown,
  Loader2,
  ExternalLink,
  Thermometer,
  Droplets,
  Wind,
  Calendar,
  Clock
} from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { WeatherService } from '../data/weatherService';

const AXON = () => {
  const navigate = useNavigate();
  const { site } = useParams();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  const [siteData, setSiteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [weatherData, setWeatherData] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Load site data
  useEffect(() => {
    const loadSiteData = async () => {
      try {
        setLoading(true);
        const dataModule = await import(`../plant-data/axon-data.json`);
        setSiteData(dataModule.default);
        setError(null);
      } catch (err) {
        console.error(`Failed to load data for site: AXON`, err);
        setError(`Site data not found for: AXON`);
      } finally {
        setLoading(false);
      }
    };

    loadSiteData();
  }, []);

  // Load weather data
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const data = await WeatherService.getCurrentWeather(27.3685177, 72.1303897);
        setWeatherData(data);
      } catch (error) {
        console.error('Weather fetch error:', error);
      }
    };

    fetchWeather();
  }, []);

  // Update clock
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Icon mapping
  const iconMap = {
    Gauge: Gauge,
    Map: Map,
    CloudFog: CloudFog,
    PanelTop: PanelTop,
    SatelliteDish: SatelliteDish,
    ActivitySquare: ActivitySquare,
    MapPin: MapPin,
    CloudFogIcon: CloudFogIcon,
    Zap: Zap,
    Shield: Shield,
    Building2: Building2
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F2F8FC] via-white to-[#E3F0F9] dark:from-[#0B1120] dark:via-[#0F172A] dark:to-[#0B1120]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-[#0084CF]" />
          <p className="text-gray-600 dark:text-slate-300">Loading AXON site information...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !siteData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 via-white to-red-100 dark:from-[#0B1120] dark:via-[#0F172A] dark:to-[#0B1120]">
        <div className="text-center p-8 bg-white dark:!bg-[#131D31] rounded-xl shadow-lg border border-red-200 dark:border-red-500/30">
          <div className="text-red-500 text-xl font-bold mb-4">Site Not Found</div>
          <p className="text-gray-600 dark:text-slate-300 mb-6">{error}</p>
          <button
            onClick={() => navigate(`/navigate/${site}`)}
            className="bg-[#0084CF] text-white px-6 py-3 rounded-lg hover:bg-[#1C4D88] transition-colors"
          >
            Back to Navigation
          </button>
        </div>
      </div>
    );
  }

  const { plantInfo, navigation, plantOverview, technicalSpecifications, ui, theme } = siteData;

  return (
    <div className={`min-h-screen pt-20 ${isDarkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-[#F2F8FC] via-white to-[#E3F0F9]'}`}>
      
      {/* Header Section */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-xl border border-white/60 p-8 mb-8"
        >
          {/* Back Button */}
          <button
            onClick={() => navigate(`/navigate/${site}`)}
            className="flex items-center gap-2 text-[#0084CF] hover:text-[#1C4D88] transition-colors mb-6"
          >
            <ArrowLeft size={20} />
            <span className="font-semibold">Back to Navigation</span>
          </button>

          {/* Site Header */}
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-gradient-to-br from-[#0084CF] to-[#0066A3] rounded-2xl shadow-lg">
                  <Building2 size={32} className="text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">{plantInfo.name}</h1>
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPin size={16} />
                    <span>{plantInfo.location}</span>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-[#F2F8FC] rounded-xl p-4">
                  <div className="text-sm text-[#0066A3] font-medium">Capacity</div>
                  <div className="text-xl font-bold text-[#1C4D88]">{plantInfo.capacity}</div>
                </div>
                <div className="bg-green-50 rounded-xl p-4">
                  <div className="text-sm text-green-600 font-medium">Type</div>
                  <div className="text-xl font-bold text-green-800">{plantInfo.type}</div>
                </div>
                <div className="bg-[#F2F8FC] rounded-xl p-4">
                  <div className="text-sm text-[#0066A3] font-medium">Commissioned</div>
                  <div className="text-xl font-bold text-[#1C4D88]">{plantInfo.commissioningDate}</div>
                </div>
                <div className="bg-orange-50 rounded-xl p-4">
                  <div className="text-sm text-orange-600 font-medium">Operator</div>
                  <div className="text-sm font-bold text-orange-800">AxonMetric</div>
                </div>
              </div>
            </div>

            {/* Weather Widget */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 shadow-lg">
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Thermometer className="w-5 h-5 text-orange-500" />
                  <span className="text-2xl font-bold text-gray-800">
                    {weatherData ? `${weatherData.temperature}°C` : '--°C'}
                  </span>
                </div>
                <div className="text-sm text-gray-600 mb-2">
                  {weatherData ? weatherData.description : 'Loading...'}
                </div>
                <div className="flex justify-center gap-4 text-xs">
                  <div className="flex items-center gap-1">
                    <Droplets className="w-3 h-3 text-[#0084CF]" />
                    <span>{weatherData ? `${weatherData.humidity}%` : '--%'}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Wind className="w-3 h-3 text-green-500" />
                    <span>{weatherData ? `${weatherData.windSpeed}m/s` : '--m/s'}</span>
                  </div>
                </div>
                <div className="mt-3 text-xs text-gray-500">
                  <div className="flex items-center justify-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span>{currentTime.toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center justify-center gap-1">
                    <Clock className="w-3 h-3" />
                    <span>{currentTime.toLocaleTimeString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Plant Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plantOverview.sections.map((section, index) => {
            const IconComponent = iconMap[section.icon] || Building2;
            const isGreen = section.isGreen;
            const primaryColor = section.primaryColor || ui.primaryColor;
            
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg border-2 p-6 hover:shadow-xl transition-all duration-300 ${
                  section.featured ? 'ring-2 ring-[#C2DFF1]' : ''
                }`}
                style={{
                  borderColor: section.featured && !isGreen ? `${primaryColor}30` : undefined
                }}
              >
                <div className="flex items-start gap-4 h-full">
                  <div className={`p-3 rounded-xl flex-shrink-0 ${
                    isGreen ? 'bg-green-100' : 'bg-[#E3F0F9]'
                  }`}
                  style={{
                    backgroundColor: !isGreen ? `${primaryColor}20` : undefined
                  }}>
                    <IconComponent size={24} className={isGreen ? 'text-green-600' : 'text-[#0066A3]'} />
                  </div>
                  <div className="flex-1 space-y-3">
                    <h3 className={`text-lg font-bold ${
                      isGreen ? 'text-green-700' : 'text-[#1C4D88]'
                    }`}>
                      {section.title}
                    </h3>
                    <div className="text-sm space-y-2 text-gray-700">
                      {section.lines.map((line, i) => {
                        if (line.includes(":")) {
                          const [label, value] = line.split(":");
                          return (
                            <div key={i} className="flex justify-between items-center">
                              <span className="font-medium text-gray-600">
                                {label.trim()}:
                              </span>
                              <span className={`font-semibold ${
                                isGreen ? 'text-green-600' : 'text-[#0066A3]'
                              }`}>
                                {value.trim()}
                              </span>
                            </div>
                          );
                        } else {
                          return (
                            <div key={i} className="text-gray-600">
                              {line}
                            </div>
                          );
                        }
                      })}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-8 bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200 p-6"
        >
          <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => navigate(`/dashboard/${site}`)}
              className="flex items-center gap-3 p-4 bg-[#F2F8FC] hover:bg-[#E3F0F9] rounded-xl transition-colors"
            >
              <Gauge size={20} className="text-[#0066A3]" />
              <span className="font-semibold text-[#1C4D88]">View Dashboard</span>
            </button>
            <button
              onClick={() => window.open(`https://www.google.com/maps?q=${plantInfo.coordinates.lat},${plantInfo.coordinates.lon}&z=15`, '_blank')}
              className="flex items-center gap-3 p-4 bg-green-50 hover:bg-green-100 rounded-xl transition-colors"
            >
              <ExternalLink size={20} className="text-green-600" />
              <span className="font-semibold text-green-800">View on Maps</span>
            </button>
            <button
              onClick={() => navigate(`/navigate/${site}`)}
              className="flex items-center gap-3 p-4 bg-[#F2F8FC] hover:bg-[#E3F0F9] rounded-xl transition-colors"
            >
              <ArrowLeft size={20} className="text-[#0066A3]" />
              <span className="font-semibold text-[#1C4D88]">Back to Portal</span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AXON;
